import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ShoppingCart, Plus, Check, Info, Search, X, Tag, Package,
    Lightbulb, Volume2, Monitor, Settings2, Zap,
    Music, Headphones, Speaker, Radio, Cpu, Wifi, Camera, Film, Star, Mic2
} from "lucide-react";
import { api, BASE } from "../services/api";
import { useCart } from "../context/CartContext";
import BookingModal from "../components/rental/BookingModal";
import Footer from "../components/landing/Footer";
import Navigation from "../components/landing/Navigation";
import SEO from "../components/seo/SEO";

const ICON_MAP = {
    Lightbulb, Volume2, Monitor, Settings2, Zap, Star,
    Music, Headphones, Speaker, Radio, Cpu, Wifi, Camera, Film, Package, Tag, Mic2
};

function hexToRgb(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r},${g},${b}`;
}

const ALL_CAT = { slug: "all", label: "Všetko", icon: Package, color: "#0099ff" };

function resolveImg(url) {
    if (!url) return null;
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

// ── Equipment card ─────────────────────────────────────────────────────────────
function RentalCard({ item, inCart, onAdd, cats }) {
    const [added, setAdded] = useState(false);
    const cat = cats[item.category] || ALL_CAT;
    const CatIcon = cat.icon;

    const handleAdd = () => {
        onAdd(item);
        setAdded(true);
        setTimeout(() => setAdded(false), 1800);
    };

    return (
        <motion.div layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="group relative bg-nova-surface/20 border border-white/8 rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300 flex flex-col">

            {/* Image */}
            <div className="relative aspect-[4/3] bg-nova-black/60 overflow-hidden">
                {item.image_url ? (
                    <img src={resolveImg(item.image_url)} alt={`${item.brand} ${item.model}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <CatIcon className="w-10 h-10 text-zinc-700" />
                    </div>
                )}
                {/* Category badge */}
                <div className="absolute top-3 left-3">
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-full border"
                        style={{ color: cat.color, borderColor: `${cat.color}30`, background: `${cat.color}12` }}>
                        {cat.label}
                    </span>
                </div>
                {/* In-cart indicator */}
                {inCart && (
                    <div className="absolute bottom-3 right-3 w-7 h-7 rounded-full bg-nova-cyan flex items-center justify-center shadow-lg">
                        <Check className="w-3.5 h-3.5 text-black" />
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 p-4">
                <div className="flex-1">
                    <div className="text-zinc-500 text-[10px] font-mono uppercase tracking-wider mb-0.5">{item.brand}</div>
                    <h3 className="font-heading font-black text-white text-base leading-tight">{item.model}</h3>
                    <div className="text-zinc-500 text-xs mt-1">{item.type}</div>

                    {/* Specs preview */}
                    {item.specs?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                            {item.specs.slice(0, 2).map((s, i) => (
                                <span key={i} className="font-mono text-[9px] bg-white/4 border border-white/6 rounded px-1.5 py-0.5 text-zinc-500">
                                    {s.k}: {s.v}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Price & CTA */}
                <div className="mt-4 pt-3 border-t border-white/6 flex items-center justify-between gap-3">
                    <div>
                        {item.rental_price_day > 0 ? (
                            <>
                                <div className="text-white font-bold text-lg leading-none">{item.rental_price_day}€</div>
                                <div className="text-zinc-600 text-[10px] font-mono">/ deň</div>
                            </>
                        ) : (
                            <div className="text-zinc-500 text-xs font-mono">Cena na dopyt</div>
                        )}
                        {item.deposit > 0 && (
                            <div className="text-zinc-600 text-[10px] mt-0.5">Záloha: {item.deposit}€</div>
                        )}
                    </div>
                    <button onClick={handleAdd}
                        className={`flex items-center gap-1.5 h-9 px-4 rounded-xl font-semibold text-sm transition-all ${
                            added
                                ? "bg-green-500/20 text-green-400 border border-green-500/30"
                                : inCart
                                    ? "bg-nova-cyan/15 text-nova-cyan border border-nova-cyan/30 hover:bg-nova-cyan/20"
                                    : "bg-nova-cyan text-black hover:saturate-125"
                        }`}>
                        {added ? (
                            <><Check className="w-3.5 h-3.5" /> Pridané</>
                        ) : inCart ? (
                            <><Plus className="w-3.5 h-3.5" /> Pridať ďalší</>
                        ) : (
                            <><Plus className="w-3.5 h-3.5" /> Do košíka</>
                        )}
                    </button>
                </div>
            </div>
        </motion.div>
    );
}

// ── Floating cart button ──────────────────────────────────────────────────────
function FloatingCart({ count, total, onClick }) {
    return (
        <AnimatePresence>
            {count > 0 && (
                <motion.button
                    initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
                    onClick={onClick}
                    className="fixed bottom-6 right-6 z-40 flex items-center gap-3 h-14 px-5 rounded-2xl bg-nova-cyan text-black font-bold shadow-[0_8px_32px_rgba(0,153,255,0.4)] hover:saturate-125 transition-all"
                >
                    <div className="relative">
                        <ShoppingCart className="w-5 h-5" />
                        <span className="absolute -top-2 -right-2 w-4.5 h-4.5 rounded-full bg-black text-white text-[9px] font-black flex items-center justify-center leading-none min-w-[18px] px-1">
                            {count}
                        </span>
                    </div>
                    <span>Rezervovať</span>
                    {total > 0 && <span className="font-mono text-black/70 text-sm">{total.toFixed(0)}€/deň</span>}
                </motion.button>
            )}
        </AnimatePresence>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function RentalPage() {
    const [items, setItems]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [cat, setCat]         = useState("all");
    const [search, setSearch]   = useState("");
    const [modalOpen, setModal] = useState(false);
    const [cats, setCats]       = useState({ all: ALL_CAT });
    const { items: cartItems, count, total, addItem, setOpen } = useCart();

    useEffect(() => {
        api.getRentableTechnika()
            .then(setItems)
            .catch(() => {})
            .finally(() => setLoading(false));
        api.getTechnikaCategories().then(list => {
            const map = { all: ALL_CAT };
            list.forEach(c => {
                map[c.slug] = { ...c, icon: ICON_MAP[c.icon_name] || Package };
            });
            setCats(map);
        }).catch(() => {});
    }, []);

    const inCartIds = new Set(cartItems.map(i => i.equipment.id));

    const filtered = items.filter(item => {
        const matchCat = cat === "all" || item.category === cat;
        const q = search.toLowerCase();
        const matchSearch = !q || `${item.brand} ${item.model} ${item.type}`.toLowerCase().includes(q);
        return matchCat && matchSearch;
    });

    const catCounts = items.reduce((acc, i) => { acc[i.category] = (acc[i.category]||0)+1; return acc; }, {});

    return (
        <div className="min-h-screen bg-nova-black text-white flex flex-col">
            <SEO
                title="Prenájom techniky"
                description="Prenajmite si profesionálne ozvučenie, osvetlenie a LED techniku pre váš event. Online rezervácia s kalendárom dostupnosti."
            />

            <Navigation />

            {/* Hero header */}
            <div className="px-6 sm:px-12 pt-28 pb-8 border-b border-white/5">
                <div className="max-w-7xl mx-auto">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-2">/ Prenájom techniky</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
                        Prenajmite si profesionálnu techniku
                    </h1>
                    <p className="text-zinc-400 text-sm max-w-xl leading-relaxed mb-5">
                        Vyberte si zariadenia, skontrolujte dostupnosť v kalendári a odošlite žiadosť o rezerváciu.
                        Potvrdíme termín do 24 hodín.
                    </p>
                    {/* Info boxes */}
                    <div className="flex flex-wrap gap-3">
                        {[
                            { icon: Package, text: "Vyzdvihnutie na sklade" },
                            { icon: Tag,  text: "Orientačné ceny online" },
                            { icon: Info, text: "Potvrdenie do 24 hodín" },
                        ].map(({ icon: Icon, text }) => (
                            <div key={text} className="flex items-center gap-2 text-xs text-zinc-500 bg-white/3 border border-white/6 rounded-lg px-3 py-1.5">
                                <Icon className="w-3.5 h-3.5 text-nova-cyan/60" />
                                {text}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="px-6 sm:px-12 py-5 border-b border-white/5 bg-nova-black/50 sticky top-[69px] z-20">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-3">
                    {/* Category filter */}
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(cats).map(([key, c]) => {
                            const count = key === "all" ? items.length : (catCounts[key]||0);
                            if (key !== "all" && count === 0) return null;
                            const Icon = c.icon;
                            return (
                                <button key={key} onClick={() => setCat(key)}
                                    className={`flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium transition-all ${
                                        cat === key ? "bg-nova-cyan text-black" : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-white/8"
                                    }`}>
                                    <Icon className="w-3 h-3" />
                                    {c.label}
                                    <span className={`font-mono text-[10px] ${cat===key?"text-black/60":"text-zinc-600"}`}>{count}</span>
                                </button>
                            );
                        })}
                    </div>
                    {/* Search */}
                    <div className="relative sm:ml-auto">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
                        <input value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Hľadať…"
                            className="h-8 pl-9 pr-8 w-full sm:w-52 rounded-lg bg-white/5 border border-white/8 text-white text-xs outline-none focus:border-nova-cyan/40" />
                        {search && (
                            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-white">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 px-6 sm:px-12 py-8">
                <div className="max-w-7xl mx-auto">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {[1,2,3,4,5,6].map(n => (
                                <div key={n} className="rounded-2xl bg-nova-surface/20 animate-pulse aspect-[3/4]" />
                            ))}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-20 text-zinc-600">
                            {items.length === 0
                                ? "Žiadna technika nie je momentálne dostupná na prenájom. Nastavte položky ako prenajímateľné v admin paneli."
                                : "Žiadne výsledky pre zvolený filter."}
                        </div>
                    ) : (
                        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {filtered.map(item => (
                                <RentalCard
                                    key={item.id}
                                    item={item}
                                    inCart={inCartIds.has(item.id)}
                                    onAdd={addItem}
                                    cats={cats}
                                />
                            ))}
                        </motion.div>
                    )}
                </div>
            </div>

            <Footer />

            {/* Floating cart */}
            <FloatingCart count={count} total={total} onClick={() => setModal(true)} />

            {/* Booking modal */}
            <AnimatePresence>
                {modalOpen && <BookingModal onClose={() => setModal(false)} />}
            </AnimatePresence>
        </div>
    );
}
